import React, { useMemo } from 'react';
import ReactSlider from 'react-slider';

type Interval = [number, number];

type MultiRangeWithColor = {
  range: Interval;
  color: string;
};

type Props = {
  disabledRanges: MultiRangeWithColor[];
  activeRange: MultiRangeWithColor | null;
  onChange: (next: Interval[]) => void;
  min?: number;
  max?: number;
  step?: number;
};

const marks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export const MultiRangeSlider: React.FC<Props> = ({
  disabledRanges,
  activeRange,
  onChange,
  min = 1,
  max = 100,
  step = 1,
}) => {
  const validRangesWithColors = useMemo(() => {
    const validRanges =
      activeRange?.range[0] && activeRange?.range[1]
        ? [activeRange, ...disabledRanges]
        : disabledRanges;
    return validRanges.sort((a, b) => a.range[0] - b.range[0]);
  }, [activeRange, disabledRanges]);
  const allRanges = useMemo(() => {
    return validRangesWithColors
      .map((item) => item.range)
      .sort((a, b) => a[0] - b[0]);
  }, [validRangesWithColors]);
  const flatValue = useMemo(() => allRanges.flat(), [allRanges]);

  const handleChange = (vals: number[]) => {
    const grouped: Interval[] = [];
    for (let i = 0; i < vals.length; i += 2) {
      grouped.push([vals[i], vals[i + 1]]);
    }
    onChange(grouped);
  };

  const getPercent = (val: number) =>
    val === 0 ? 0 : ((val - min) / (max - min)) * 100;

  return (
    <div className="w-full pt-6 relative">
      <div className="absolute left-0 right-0 top-0">
        {marks.map((mark) => (
          <div
            key={mark}
            className="absolute -translate-x-1/2"
            style={{ left: `${getPercent(mark)}%` }}
          >
            <span className="text-xs text-gray-500 !-translate-x-1/2 block">
              {mark}
            </span>
            <div className="w-px h-2 bg-gray-300 mb-1" />
          </div>
        ))}
      </div>
      <ReactSlider
        value={flatValue}
        onChange={handleChange}
        min={min}
        minDistance={1}
        max={max}
        step={step}
        withTracks
        pearling={false}
        renderTrack={(trackProps, state) => {
          const isInterval = state.index % 2 === 0;
          const thumbValue = flatValue[state.index];
          const isLocked =
            !activeRange ||
            thumbValue < activeRange.range[0] ||
            thumbValue > activeRange.range[1];
          return (
            <div
              {...trackProps}
              style={{
                ...trackProps.style,
                height: 39,
                pointerEvents: isLocked ? 'none' : 'auto',
                backgroundColor: isInterval
                  ? 'rgba(224, 210, 191, 0.3)'
                  : validRangesWithColors[Math.floor(state.index / 2)]?.color ||
                    'rgba(224, 210, 191, 0.3)',
              }}
            />
          );
        }}
        renderThumb={(thumbProps, state) => {
          const thumbValue = flatValue[state.index];
          const isLocked =
            !activeRange ||
            thumbValue < activeRange.range[0] ||
            thumbValue > activeRange.range[1];

          return (
            <div
              {...thumbProps}
              style={{
                ...thumbProps.style,
                height: 39,
                width: 2,
                cursor: 'pointer',
                pointerEvents: isLocked ? 'none' : 'auto',
              }}
            />
          );
        }}
        className="relative w-full"
      />
    </div>
  );
};
